import { daysInRange } from '../date-range.util'

export const EXPENSE_COMPOSITION_GRANULARITIES = ['day', 'week', 'month', 'year'] as const

export type ExpenseCompositionGranularity = (typeof EXPENSE_COMPOSITION_GRANULARITIES)[number]

// widest range (inclusive days) each granularity may be requested for.
// `day` and `week` are capped so the stacked chart never renders hundreds of
// unreadable bars and the response stays bounded; `month`/`year` need no cap of
// their own because the 36-month range cap already bounds them (36 / 3 bars).
// 92 days covers any 3-calendar-month span, 731 covers 2 years across a leap year.
export const GRANULARITY_MAX_RANGE_DAYS: Record<ExpenseCompositionGranularity, number | null> = {
  day: 92,
  week: 731,
  month: null,
  year: null,
}

// range widths (inclusive days) at which the auto-derived bucket steps up.
// deliberately narrower than the caps above: `day` is auto-picked only up to a
// month but stays MANUALLY reachable up to three, which is the band the cap guards.
const AUTO_DAY_MAX_DAYS = 31
const AUTO_WEEK_MAX_DAYS = 92
const AUTO_MONTH_MAX_DAYS = 731

/** true when the granularity may be requested for the given range */
export const isGranularityAllowed = (
  fromIso: string,
  toIso: string,
  granularity: ExpenseCompositionGranularity
): boolean => {
  const max = GRANULARITY_MAX_RANGE_DAYS[granularity]
  if (max === null) return true
  return daysInRange(fromIso, toIso) <= max
}

/** the bucket a range gets when the user has not picked one explicitly */
export const autoGranularity = (fromIso: string, toIso: string): ExpenseCompositionGranularity => {
  const days = daysInRange(fromIso, toIso)
  if (days <= AUTO_DAY_MAX_DAYS) return 'day'
  if (days <= AUTO_WEEK_MAX_DAYS) return 'week'
  if (days <= AUTO_MONTH_MAX_DAYS) return 'month'
  return 'year'
}

/**
 * Resolves the bucket to show after a range change. A manual pick survives as
 * long as it is still offered for the new range; otherwise it falls back to the
 * auto-derived bucket (and stops being manual).
 */
export const resolveGranularity = (
  fromIso: string,
  toIso: string,
  current: ExpenseCompositionGranularity | null
): ExpenseCompositionGranularity =>
  current !== null && isGranularityAllowed(fromIso, toIso, current)
    ? current
    : autoGranularity(fromIso, toIso)
